import { Component, OnInit, HostListener, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { formatCurrency, AsyncPipe } from '@angular/common';
import { Observable, switchMap, tap } from 'rxjs';

import { EntWindowRefService } from '@ng/ent-utilities';

import { ContractDetailsService } from 'src/app/shared/services/contract-details.service';
import { AgentService } from 'src/app/shared/services/agent.service';
import { UtilsService } from 'src/app/shared/services/utils.service';

import {
  AgentData,
  EntPrimaryTableConfigInterface,
  ResponseStatusInterface,
  RateLockInfoData,
  ContractData,
  ContractStatusTracker,
  BRERequirement,
  FundsReceivedMoneyIn,
  SourceOfFundsUI,
  Reg60StatusTracker,
} from 'src/app/shared/models/app.model';

import {
  AppConstants,
  Message,
  ResponseStatus,
  SECURE_SITES,
  Reg60ChevronSteps
} from 'src/config/constants';
import { ClientInfoTableConfig } from '../client-information/client-info-table-config';
import { AgentInfoTableConfig } from '../agent-information/agent-info-table-config';
import { ContractInfoTableConfig } from '../contract-information/contract-info-table-config';
import { SourceOfFundsTableConfig } from '../source-of-fund/source-of-fund-table-config';
import { FundsReceivedTableConfig } from '../funds-received/funds-received-table-config';
import { AnalyticsAppService } from 'src/app/shared/services/analytics.service';
import { FileMetadataService } from 'src/app/shared/services/file-metadata.service';
import { EntFormWidgetsModule } from '@ng/ent-components/ent-form-widgets';
import { TrackerTabsComponent } from '../tracker-tabs/tracker-tabs.component';
import { ClientInformationComponent } from '../client-information/client-information.component';
import { EntTablesModule } from '@ng/ent-components/ent-tables';
import { RequirementComponent } from '../requirement/requirement.component';
import { AgentInformationComponent } from '../agent-information/agent-information.component';
import { ContractInformationComponent } from '../contract-information/contract-information.component';
import { RateLockInformationComponent } from '../rate-lock-information/rate-lock-information.component';
import { SourceOfFundComponent } from '../source-of-fund/source-of-fund.component';
import { FundsReceivedComponent } from '../funds-received/funds-received.component';

@Component({
    selector: 'app-policy-details',
    templateUrl: './policy-details.component.html',
    styleUrls: ['./policy-details.component.scss'],
    imports: [EntFormWidgetsModule, ClientInformationComponent, EntTablesModule, RequirementComponent, AgentInformationComponent, ContractInformationComponent, RateLockInformationComponent, SourceOfFundComponent, FundsReceivedComponent, AsyncPipe, TrackerTabsComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PolicyDetailsComponent implements OnInit {
  private readonly agentService = inject(AgentService);
  private readonly contractDetailsService = inject(ContractDetailsService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly utilService = inject(UtilsService);
  private readonly analytics = inject(AnalyticsAppService);
  private readonly nativeWindow = inject(EntWindowRefService);
  private readonly fileMetadataService = inject(FileMetadataService);

  apiStatus$: Observable<string> | undefined;
  message = Message;
  responseStatus: ResponseStatusInterface = ResponseStatus;
  contractNumber!: number;
  clientInfoTableConfig: EntPrimaryTableConfigInterface = ClientInfoTableConfig;
  contractData!: ContractData;
  agentData!: AgentData[];
  agentInfoTableConfig: EntPrimaryTableConfigInterface = AgentInfoTableConfig;
  contractInfoTableConfig: EntPrimaryTableConfigInterface =
    ContractInfoTableConfig;
  rateLockInfo!: RateLockInfoData;
  sourceOfFundsTableConfig: EntPrimaryTableConfigInterface =
    SourceOfFundsTableConfig;
  fundsReceivedTableConfig: EntPrimaryTableConfigInterface =
    FundsReceivedTableConfig;
  contractTrackerData!: ContractStatusTracker[];
  requirementsData!: BRERequirement[];
  noLOA = false;
  notNoLOA = false;
  isAccountLookup: boolean = false;
  mobileView: boolean = false;
  reg60TrackerData: Reg60StatusTracker[] = [];

  win: any = '';
  currentFocus = '';

  constructor() {
    this.win = this.nativeWindow.getWindow();
  }

  ngOnInit(): void {
    window.scroll(0, 0);
    const contractNumberParam = this.activatedRoute.snapshot.queryParamMap.get('contractNumber');
    this.contractNumber = contractNumberParam ? Number(contractNumberParam) : 0;
    this.contractDetailsService.setContractNumber(this.contractNumber);
    this.apiStatus$ = this.agentService.agentProfileSubject.pipe(
      tap((agentResponse: any) => agentResponse),
      switchMap((agentResponse: any) =>
        agentResponse
          ? this.contractDetailsService.fetchContractDetails(agentResponse)
          : ''
      ),
      tap((contractRespone: any) => {
        if (contractRespone === this.responseStatus.success) {
          this.loadAppData();
          this.analytics.logPageView('contract details');
        }
      })
    );
    this.agentService.fetchAgentProfile();
    this.isAccountLookup = this.contractDetailsService.getAccountlookupFlag();
    this.checkMobileView();
  }

  /**
   * Loads data from the contract details service and assigns it to the component properties.
   */
  loadAppData(): void {
    this.contractData = this.contractDetailsService.getContractDetails();
    const clientName = `${this.contractData?.ClientLastName ?? ''}, ${
      this.contractData?.ClientFirstName ?? ''
    }`;
    this.utilService.setOwnerName(clientName);
    this.utilService.setBRERequirements(this.contractData.BRERequirement);
    this.utilService.setNIGOComments(this.contractData.NIGOComment);
    this.utilService.setAgentComments(this.contractData.AgentComment);
    this.clientInfoTableConfig.data = this.getClientInfoData();
    this.agentData = this.contractData?.AgentInfo;
    this.agentInfoTableConfig.data = this.agentData;
    this.requirementsData = this.getRequirementData();
    this.contractInfoTableConfig.data = this.getContractInfoData();
    this.rateLockInfo = this.getRateLockInfoData();
    this.sourceOfFundsTableConfig.data = this.getSourceOfFundsInfoData();
    this.fundsReceivedTableConfig.data = this.getFundsReceivedInfoData();
    this.contractTrackerData = this.contractData?.ContractStatusTracker;
    this.reg60TrackerData = this.processReg60StatusTracker(
      this.contractData?.Reg60StatusTracker
    );
    this.checkLOACondition();
    this.utilService.setFilesToDownload(this.contractData?.filesToDownload);
    this.agentService.setHantzReadOnly(this.contractData.HantzReadOnly);
  }

  /**
   * Normalizes required REG60 trackers by filling each ordered chevron through
   * the furthest reported step and marking the preceding positions completed.
   */
  processReg60StatusTracker(
    reg60StatusTracker: Reg60StatusTracker[] | undefined
  ): Reg60StatusTracker[] {
    const chevronIndexes = new Map(
      Reg60ChevronSteps.map((chevron, index) => [
        this.normalizeReg60Chevron(chevron),
        index,
      ])
    );

    return (reg60StatusTracker || []).map((tracker) => {
      if (!tracker.Required) {
        return { ...tracker, TrackerSteps: [] };
      }

      const trackerSteps = tracker.TrackerSteps || [];
      const stepsByChevron = new Map<string, (typeof trackerSteps)[number]>();
      let lastCompletedIndex = -1;

      trackerSteps.forEach((step) => {
        const normalizedChevron = this.normalizeReg60Chevron(
          step.TrackerChevron
        );
        const chevronIndex = chevronIndexes.get(normalizedChevron);

        if (!stepsByChevron.has(normalizedChevron)) {
          stepsByChevron.set(normalizedChevron, step);
        }
        if (chevronIndex !== undefined && chevronIndex > lastCompletedIndex) {
          lastCompletedIndex = chevronIndex;
        }
      });

      return {
        ...tracker,
        TrackerSteps: Reg60ChevronSteps.map((chevron, index) => {
          const existingStep = stepsByChevron.get(
            this.normalizeReg60Chevron(chevron)
          );

          return {
            TrackerChevron: chevron,
            ChevronStatus: index <= lastCompletedIndex ? 'Completed' : 'Pending',
            StatusDate: existingStep?.StatusDate ?? '',
          };
        }),
      };
    });
  }

  private normalizeReg60Chevron(chevron: string): string {
    return chevron
      ?.split('/')
      .map((part) => part.trim())
      .join('/')
      .toLowerCase();
  }

  /**
   * Formats client info data for use in the client info table
   *
   * @returns formatted array
   */
  getClientInfoData(): Array<any> {
    const clientInfo = this.contractData?.OwnerAnnuitantInfo || null;
    return clientInfo?.map((data: any) => {
      let clientName = `${data?.LastName ?? ''}, ${data?.FirstName ?? ''}`;
      clientName = this.utilService.getFormattedName(clientName);
      return {
        ...data,
        FullName: data?.FullName ? data?.FullName : clientName,
        PhoneNumber: this.utilService.getFormattedPhoneNumber(data.PhoneNumber),
      };
    });
  }

  /**
   * Formats rate lock info data for use in the rate lock info table
   *
   * @returns object with formatted data
   */
  getRateLockInfoData(): any {
    let wait30 = '';
    if (this.contractData?.Wait30 === 'Y') {
      wait30 = 'Elected';
    } else if (this.contractData?.Wait30 === 'N') {
      wait30 = 'Not elected';
    }
    return {
      effectiveDate: this.contractData?.RateLockEffDate
        ? this.utilService.getFormattedDate(this.contractData?.RateLockEffDate)
        : '',
      duration: this.contractData?.RateLockDuration
        ? `${this.contractData?.RateLockDuration} Days`
        : '',
      expirationDate: this.contractData?.RateLockExpireDate
        ? this.utilService.getFormattedDate(
            this.contractData?.RateLockExpireDate
          )
        : '',
      wait30: wait30,
    };
  }

  /**
   * Sets agent info table config to display all rows
   */
  viewAllrows(): void {
    this.agentInfoTableConfig.config.rowsPerPage =
      this.agentInfoTableConfig.data.length;
    this.setFocus('viewless');
  }

  /**
   * Sets agent info table config to display 3 rows
   */
  viewLessrows(): void {
    this.agentInfoTableConfig.config.rowsPerPage = 3;
    this.setFocus('viewall');
  }

  /**
   * Formats contract info data to use in the contract info table
   * The global table component is designed for row-oriented tables
   * This method maps the data to display in a column-oriented way
   *
   * @returns array of key-value pairs for each row in the table
   */
  getContractInfoData(): Array<any> {
    const issueStateAriaLabel =
      [...(this.contractData?.IssueState || '')]?.join(',') || '';
    const rows = {
      Type: this.contractData?.ProductType,
      Plan: this.utilService.addTrademarkSymbol(
        this.contractData?.LegalProductName
      ),
      'Total expected amount':
        this.contractData?.TotalExpectedAmt ||
        this.contractData?.TotalExpectedAmt == 0
          ? formatCurrency(
              Number(this.contractData?.TotalExpectedAmt),
              'en-US',
              '$'
            )
          : '',
      'Application received date': this.contractData?.AppReceivedDate
        ? this.utilService.getFormattedDate(this.contractData?.AppReceivedDate)
        : '',
      'Issue state': `<span aria-hidden="true">${
        this.contractData?.IssueState || ''
      }</span><span class="screen-reader-only">${issueStateAriaLabel}</span>`,
      'Contract status': this.contractData?.ContractStatus,
      'Client name': this.utilService.getOwnerName(),
    };

    return Object.entries(rows)?.map(([key, val]) => {
      return {
        label: key,
        value: val,
      };
    });
  }

  /**
   * Formats source of funds data for use in the source of funds table
   *
   * @returns array of SourceOfFundsUI objects
   */
  getSourceOfFundsInfoData(): Array<SourceOfFundsUI> {
    const sourceOfFunds = this.contractData?.SourceOfFundsUI || [];
    const formattedSourceOfFunds = sourceOfFunds?.map((data) => {
      const paperWorkSentDate = data?.PaperworkSentDate
        ? this.utilService.getFormattedDate(data?.PaperworkSentDate)
        : '';
      let deliveryMethod = '';
      if (paperWorkSentDate && data?.DeliveryMethod) {
        if (data?.DeliveryMethod?.indexOf(AppConstants.DTCCC2C) === -1) {
          deliveryMethod = `<b>${data.DeliveryMethod.charAt(0).toUpperCase()}${data.DeliveryMethod.slice(1).toLowerCase()}</b><br>`;
        } else {
          deliveryMethod = `<b>${data?.DeliveryMethod}</b><br>`;
        }
      }
      const faxNumber =
        (deliveryMethod && data?.DeliveryMethod?.indexOf(AppConstants.FAXED) !== -1 && data?.FaxNumber)
          ? `${this.utilService.getFormattedFaxNumber(data.FaxNumber)}<br>`
          : '';
      return {
        ...data,
        PaperworkSent: `${deliveryMethod}${faxNumber}${paperWorkSentDate}`,
        FundsReceived: data?.FundsReceivedDate
          ? this.utilService.getFormattedDate(data?.FundsReceivedDate)
          : '',
      };
    });
    const hasCarrierCallTag = sourceOfFunds.some((data) => data?.CarrierCall);
    if (!hasCarrierCallTag) {
      this.sourceOfFundsTableConfig.column =
        this.sourceOfFundsTableConfig.column?.filter((col) => {
          return col.columnKey !== 'CarrierCall';
        });
    }
    return formattedSourceOfFunds;
  }

  /**
   * Formats source of funds data for use in the funds received table
   *
   * @returns array of FundsReceivedMoneyIn objects
   */
  getFundsReceivedInfoData(): Array<FundsReceivedMoneyIn> {
    const fundsReceived = this.contractData?.FundsReceivedMoneyIn || null;
    return fundsReceived?.map((data) => {
      return {
        ...data,
        ClientName: this.utilService.getOwnerName(),
      };
    });
  }

  /**
   * Formats requirement data for use in the requirements component
   *
   * @returns array of BRERequirement objects
   */
  getRequirementData(): Array<BRERequirement> {
    const requirements = this.contractData?.BRERequirement || null;
    return requirements?.map((data) => {
      return {
        ...data,
        Action: data?.RequirementSeverity === 'INFO' ? 'REVIEW' : 'RESOLVE',
        RequirementDate: data?.RequirementDate
          ? this.utilService.getFormattedDate(data?.RequirementDate)
          : '',
        RequirementStatusDate: data?.RequirementStatusDate
          ? this.utilService.getFormattedDate(data?.RequirementStatusDate)
          : '',
        Comment: data?.RequirementSeverity === 'INFO' ? '' : 'VIEW COMMENTS',
      };
    });
  }

  /**
   * Checks for 'No LOA' payment type in SourceOfFundsDTCC and SourceOfFundsPpwk data
   */
  checkLOACondition(): void {
    const sourceOfFundsDTCC = this.contractData?.SourceOfFundsDTCC || null;
    const sourceOfFundsPpwk = this.contractData?.SourceOfFundsPpwk || null;
    this.noLOA =
      sourceOfFundsDTCC?.filter((data) => {
        return data.PaymentType === 'No LOA';
      }).length > 0 ||
      sourceOfFundsPpwk?.filter((data) => {
        return data.PaymentType === 'No LOA';
      }).length > 0;

    this.notNoLOA =
      (sourceOfFundsDTCC && sourceOfFundsDTCC?.length > 0
        ? this.filterObjForNoLOA(sourceOfFundsDTCC)
        : true) &&
      (sourceOfFundsPpwk && sourceOfFundsPpwk?.length > 0
        ? this.filterObjForNoLOA(sourceOfFundsPpwk)
        : true);
  }

  /**
   * Filter Object in SourceOfFundsDTCC and SourceOfFundsPpwk data for not 'No LOA'
   */
  filterObjForNoLOA(obj: any): boolean {
    return (
      obj?.filter((data: any) => {
        return data.PaymentType !== 'No LOA';
      }).length > 0
    );
  }

  /**
   * set focus on the required element
   */
  setFocus(elementId: string): void {
    setTimeout(() => {
      this.win.document.getElementById(elementId)?.focus();
    }, 200);
  }

  /**
   * build the pending or account lookup URL and redirect back to respective URL
   */
  backToSearchResults(): void {
    const siteName = (window as { [key: string]: any })[
      AppConstants.SITE_NAME
    ] as keyof typeof SECURE_SITES;
    let navURL = `/${AppConstants.SITE_TYPE}${SECURE_SITES[siteName]}`;
    if (this.isAccountLookup) {
      navURL += `${AppConstants.ACCOUNT_LOOKUP}?selectedTab=1&fromPage=annuity-pending`;
    } else {
      navURL += AppConstants.HUB_PENDING;
      this.analytics.logAnalytics({
        event_action: 'link',
        event_name: 'back to search results',
        event_type: 'pending tool - annuity',
      });
    }
    this.win.open(navURL, '_self');
  }

  /**
   * Checks the window's inner width to determine if page is rendering on a mobile device
   */
  @HostListener('window:resize')
  checkMobileView() {
    this.mobileView = window.innerWidth < 750;
  }
}
